import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { createRoot } from 'react-dom/client';
import { motion, AnimatePresence } from 'motion/react';
import { Icon } from './Icon';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { toastEntrance } from './primitives/motion';
import styles from './Message.module.css';

type MessageType = 'info' | 'success' | 'warning' | 'error' | 'loading';

interface MessageConfig {
  content: React.ReactNode;
  duration?: number;
  key?: string;
  onClose?: () => void;
}

interface MessageInstance {
  destroy: () => void;
}

const iconMap: Record<MessageType, { icon: React.ReactNode; colorClass: string }> = {
  info: { icon: <Icon name="info" size={16} />, colorClass: styles.iconInfo },
  success: { icon: <Icon name="check_circle" size={16} />, colorClass: styles.iconSuccess },
  warning: { icon: <Icon name="warning" size={16} />, colorClass: styles.iconWarning },
  error: { icon: <Icon name="cancel" size={16} />, colorClass: styles.iconError },
  loading: { icon: <Icon name="progress_activity" size={16} />, colorClass: styles.iconLoading },
};

const messageItem = cva(styles.item);

function MessageItem({
  type,
  content,
  onRemove: _onRemove,
}: {
  type: MessageType;
  content: React.ReactNode;
  onRemove: () => void;
}) {
  const reducedMotion = useReducedMotion();
  const config = iconMap[type];

  return (
    <motion.div
      initial={reducedMotion ? { opacity: 0 } : toastEntrance.initial}
      animate={reducedMotion ? { opacity: 1 } : toastEntrance.animate}
      exit={reducedMotion ? { opacity: 0 } : toastEntrance.exit}
      transition={reducedMotion ? { duration: 0 } : toastEntrance.transition}
      className={messageItem()}
    >
      <span className={clsx(styles.iconWrapper, config.colorClass, type === 'loading' && styles.spinning)}>
        {config.icon}
      </span>
      {content}
    </motion.div>
  );
}

// ── Container management ─────────────────────────────────────────────────────

const MAX_MESSAGES = 3;

let containerEl: HTMLDivElement | null = null;
let containerRoot: ReturnType<typeof createRoot> | null = null;
let messages: { id: number; key?: string; type: MessageType; content: React.ReactNode; remove: () => void }[] = [];
let nextId = 0;

function ensureContainer() {
  if (!containerEl) {
    containerEl = document.createElement('div');
    containerEl.className = styles.container;
    document.body.appendChild(containerEl);
    containerRoot = createRoot(containerEl);
  }
}

function renderMessages() {
  if (!containerRoot) return;
  containerRoot.render(
    <AnimatePresence>
      {messages.map((m) => (
        <MessageItem key={m.id} type={m.type} content={m.content} onRemove={m.remove} />
      ))}
    </AnimatePresence>,
  );
}

function addMessage(type: MessageType, config: string | MessageConfig): MessageInstance {
  ensureContainer();
  const cfg = typeof config === 'string' ? { content: config } : config;
  const duration = cfg.duration ?? (type === 'loading' ? 0 : 3000);
  const id = nextId++;

  let timer: ReturnType<typeof setTimeout> | null = null;

  const remove = () => {
    messages = messages.filter((m) => m.id !== id);
    renderMessages();
    cfg.onClose?.();
  };

  // Key dedup: replace existing message with same key
  if (cfg.key) {
    const existing = messages.find((m) => m.key === cfg.key);
    if (existing) existing.remove();
  }

  // Cap: drop oldest when at limit
  if (messages.length >= MAX_MESSAGES) {
    messages[0].remove();
  }

  messages = [...messages, { id, key: cfg.key, type, content: cfg.content, remove }];
  renderMessages();

  if (duration > 0) {
    timer = setTimeout(remove, duration);
  }

  return {
    destroy: () => {
      if (timer) clearTimeout(timer);
      remove();
    },
  };
}

/**
 * message — programmatic toast notifications.
 *
 * | Method | Args | Description |
 * |--------|------|-------------|
 * | message.info(content, duration?) | string \| MessageConfig | Info toast |
 * | message.success(content, duration?) | string \| MessageConfig | Success toast |
 * | message.warning(content, duration?) | string \| MessageConfig | Warning toast |
 * | message.error(content, duration?) | string \| MessageConfig | Error toast |
 * | message.loading(content) | string \| MessageConfig | Loading toast (no auto-close) |
 *
 * Returns `{ destroy() }` to manually dismiss.
 *
 * @example
 * message.success('Saved!');
 * message.error({ content: 'Failed to save', duration: 5000 });
 * const hide = message.loading('Syncing...');
 * // later: hide.destroy();
 */
export const message = {
  info: (config: string | MessageConfig) => addMessage('info', config),
  success: (config: string | MessageConfig) => addMessage('success', config),
  warning: (config: string | MessageConfig) => addMessage('warning', config),
  error: (config: string | MessageConfig) => addMessage('error', config),
  loading: (config: string | MessageConfig) => addMessage('loading', config),
};
